import { useState } from 'react';
import { Button, Card, Checkbox, Col, Divider, Flex, Form, Input, Row, Switch } from 'antd';
import type { FormProps } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

// services
import NotificationService from 'core/services/notification';

// components
import PageTitle from 'components/title/title';
import FormElement from 'components/formElement/formElement';
import PhoneInputComponent, { PhoneInputReturn } from 'components/phoneInput/phoneInput';
import { checkPhoneValidation } from 'utils/validators/phone';
import SelectElement from 'components/selectElement/selectElement';
import DatePickerElement from 'components/datePicker/datePicker';
import { NATIONALITIES } from 'utils/nationalities';
import { isRTL } from 'core/services/language';
import StatusComponent from 'components/status/status';
import Icon from 'utils/icon';

function BusinessMortgageCreatePage() {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [count, setCount] = useState<number>(0);
	const [availableShares, setAvailableShares] = useState<number>(57);
	const [isCheckLoading, setIsCheckLoading] = useState(false);
	const [isChecked, setIsChecked] = useState(false);
	const [isCreateLoading, setIsCreateLoading] = useState(false);
	const [isMaturityDate, setIsMaturityDate] = useState<boolean>(false);
	const [acknowledge, setAcknowledge] = useState<boolean>(false);

	const [checkForm] = Form.useForm();
	const [createForm] = Form.useForm();

	type checkFieldType = {
		national_id: number;
		commercial_reg: number;
	};

	type createFieldType = {
		name: string;
		phone: number;
		national_id: number;
		board: string;
		executive: boolean;
		birthdate: string;
		nationality: string;
		founder: boolean;
	};

	const onChangePhone = ({ dialCode, number }: PhoneInputReturn) => {
		createForm.setFieldsValue({
			phone: dialCode + number,
		});
	};

	const validatePhone = (_: any, value: any) => {
		// if (checkPhoneValidation(value)) {
		// 	return Promise.resolve();
		// } else if (value) return Promise.reject(new Error(t('COMMON.PHONE_INVALID')));
		// else return Promise.reject();
	};

	const modifyCount = (operation: 'plus' | 'minus') => {
		if (operation === 'plus') {
			setCount(count + 1);
		} else {
			if (count > 0) setCount(count - 1);
		}
	};

	const validateCount = (e: any) => {
		if (e.target.value > availableShares) {
			setCount(availableShares);
			return;
		}

		setCount(e.target.value);
	};

	const onCheckFinish: FormProps<checkFieldType>['onFinish'] = async (values) => {
		try {
			setIsCheckLoading(true);
			setTimeout(() => {
				setIsCheckLoading(false);
				setIsChecked(true);
			}, 1000);
		} catch (error: any) {
			NotificationService.error('Ops !!', 'Error');
		}
	};

	const onCreateFinish: FormProps<createFieldType>['onFinish'] = async (values) => {
		try {
		} catch (error: any) {
			NotificationService.error('Ops !!', 'Error');
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.CREATE_MORTGAGE')} />

			<Card className="mt-3">
				<div className="mb-3">
					<h5>{t('MORTGAGE.CREATE_NEW_REQUEST')}</h5>
					<p className="text-light">{t('MORTGAGE.FILL_DATA')}</p>
				</div>

				<Form form={checkForm} onFinish={onCheckFinish}>
					<Row gutter={[16, 16]}>
						<Col span={24} md={12}>
							<FormElement fullWidth type="number" name="nationalId" label={t('COMMON.NATIONAL_ID')} rules={[{ required: true }]} />
						</Col>

						<Col span={24} md={12}>
							<FormElement
								fullWidth
								type="number"
								name="commercial_reg"
								label={t('MORTGAGE.COMMERCIAL_REGISTRATION_NUMBER')}
								rules={[{ required: true }]}
							/>
						</Col>

						{!isChecked && (
							<Col span={24}>
								<Flex justify="flex-end">
									<Button type="primary" loading={isCheckLoading} htmlType="submit" className="px-4" size="middle">
										{t('COMMON.CHECK')}
									</Button>
								</Flex>
							</Col>
						)}
					</Row>
				</Form>

				{isChecked && (
					<>
						<Divider />

						<Form form={createForm} onFinish={onCreateFinish}>
							<Row gutter={[16, 16]}>
								<Col span={24} md={12} lg={8}>
									<FormElement disabled type="text" name="fullname" label={t('COMMON.FULL_NAME')} rules={[{ required: true }]} />
								</Col>

								<Col span={24} md={12} lg={8}>
									<Form.Item name="phone" rules={[{ required: true, message: t('COMMON.PHONE_REQUIRED') }, { validator: validatePhone }]}>
										<PhoneInputComponent onChange={onChangePhone} />
									</Form.Item>
								</Col>

								<Col span={24} md={12} lg={8}>
									<FormElement type="text" name="national_id" label={t('COMMON.NATIONAL_ID')} rules={[{ required: true }]} />
								</Col>

								<Col span={24} md={12} lg={8}>
									<SelectElement label={t('MORTGAGE.BOARD')} options={[{ value: 'member', label: t('MORTGAGE.MEMBER') }]} />
								</Col>

								<Col span={24} md={12} lg={8}>
									<SelectElement
										label={t('COMMON.EXECUTIVE')}
										options={[
											{ value: true, label: t('COMMON.YES') },
											{ value: false, label: t('COMMON.NO') },
										]}
									/>
								</Col>

								<Col span={24} md={12} lg={8}>
									<DatePickerElement label={t('COMMON.DATE_OF_BIRTH')} onChange={() => {}} />
								</Col>

								<Col span={24} md={12} lg={8}>
									<SelectElement
										label={t('MORTGAGE.NATIONALITY')}
										options={NATIONALITIES.map((item) => {
											return { label: isRTL() ? item.ar : item.en, value: item.en };
										})}
									/>
								</Col>

								<Col span={24} md={12} lg={8}>
									<SelectElement
										label={t('COMMON.FOUNDER')}
										options={[
											{ value: true, label: t('COMMON.YES') },
											{ value: false, label: t('COMMON.NO') },
										]}
									/>
								</Col>
							</Row>
						</Form>

						<Card className="mt-5">
							<Flex justify="space-between" align="center">
								<Flex align="center" gap={10}>
									{/* <img src={sample1} alt="company" style={{ width: 40, height: 40, borderRadius: '50%' }} /> */}
									<h6 className="fw-semibold mb-0">Company name</h6>
								</Flex>

								<Button type="primary" size="middle" onClick={() => navigate('/business/dashboard/companies/12345')}>
									{t('MORTGAGE.COMPANY_DOCUMENTS')}
								</Button>
							</Flex>

							<Row gutter={[16, 16]} className="mt-4">
								<Col span={24} md={12}>
									<FormElement
										fullWidth
										name="common_shares"
										label={t('COMMON.COMMON_SHARES')}
										type="number"
										addonAfter={
											<StatusComponent style={{ paddingBlock: 3 }} bordered color="green" small text={`${t('COMMON.AVAILABLE')}: ${57}`} />
										}
									/>
								</Col>

								<Col span={24} md={12}>
									<FormElement
										fullWidth
										name="preferred_shares"
										label={t('COMMON.PREFERRED_SHARES')}
										type="number"
										addonAfter={
											<StatusComponent style={{ paddingBlock: 3 }} bordered color="green" small text={`${t('COMMON.AVAILABLE')}: ${57}`} />
										}
									/>
								</Col>
							</Row>

							<Flex gap={30} className="mt-3">
								<div>
									<span className="d-block mb-1">{t('MORTGAGE.TOTAL_SHARES_HOLD')}</span>
									<p className="fw-semibold">50</p>
								</div>
								<div>
									<span className="d-block mb-1">{t('MORTGAGE.ON')}</span>
									<p className="fw-semibold">24-07-2024</p>
								</div>
								<div>
									<span className="d-block mb-1">{t('MORTGAGE.HOLD_TO')}</span>
									<p className="fw-semibold">Riyadh Bank</p>
								</div>
								<div>
									<span className="d-block mb-1">{t('COMMON.STATUS')}</span>
									<StatusComponent small text={t('COMMON.ACTIVE')} color="green" />
								</div>
							</Flex>
						</Card>

						<div className="mt-4">
							<h6>{t('MORTGAGE.HOLD_SHARES')}</h6>

							<p>{t('MORTGAGE.TOTAL_SHARES_NUM')}</p>
							<Flex gap={10} align="center" className="containerDiv w-max p-2">
								<Button type="primary" className="h-max" style={{ padding: 5 }} onClick={() => modifyCount('minus')} disabled={count === 0}>
									<Icon name="minus" color="#fff" width={12} height={12} />
								</Button>

								<Input
									variant="borderless"
									type="number"
									min={0}
									max={availableShares}
									style={{ width: 50, padding: 0, textAlign: 'center' }}
									value={count}
									onInput={validateCount}
								/>

								<Button
									type="primary"
									className="h-max"
									style={{ padding: 5 }}
									onClick={() => modifyCount('plus')}
									disabled={count == availableShares}>
									<Icon name="plus" color="#fff" width={12} height={12} />
								</Button>
							</Flex>
						</div>

						<Flex gap={40} align="center" wrap className="mt-5" style={{ minHeight: 40 }}>
							<div>
								<Switch checked={isMaturityDate} onChange={() => setIsMaturityDate(!isMaturityDate)} />
								<span style={{ marginInlineStart: 5, fontWeight: 500 }}>{t('MORTGAGE.MATURITY_DATE')}</span>
							</div>

							{isMaturityDate && <DatePickerElement onChange={() => {}} />}
						</Flex>

						<Flex justify="space-between" align="center" className="mt-5">
							<Checkbox onChange={(event) => setAcknowledge(event.target.checked)}>
								<span className="fw-semibold d-block">{t('MORTGAGE.AGREEMENT_1')}</span>
								<span className="text-light">{t('MORTGAGE.AGREEMENT_2')}</span>
							</Checkbox>
							<Button type="primary" className="px-4" disabled={!acknowledge} size="middle">
								{t('COMMON.SUBMIT')}
							</Button>
						</Flex>
					</>
				)}
			</Card>
		</>
	);
}

export default BusinessMortgageCreatePage;
